// Resolve template files bundled inside the Avatar CLI package. tsup bundles
// src/ into dist/index.js but does NOT bundle template files — they ship as
// loose files via package.json `files` field (src/templates, src/hooks).
import { existsSync } from "node:fs";
import { dirname, join } from "node:path";
import { fileURLToPath } from "node:url";
import { readText } from "./filesystem-helpers.js";
import { renderTemplate } from "./mustache-template-engine.js";

// Templates ship in src/templates/ and src/hooks/ at runtime (see
// package.json `files` field — both src/templates and src/hooks are included
// in the npm tarball). Locate them by walking up from this file to the
// package root (folder containing package.json), then descending to src/.
//
// This works identically from dist/index.js (npm-installed) and from
// src/lib/*.ts (vitest/tsx) without path string sniffing.
const HERE = dirname(fileURLToPath(import.meta.url));
const PACKAGE_ROOT = findPackageRoot(HERE);
const TEMPLATES_ROOT = join(PACKAGE_ROOT, "src", "templates");
const HOOKS_ROOT = join(PACKAGE_ROOT, "src", "hooks");

function findPackageRoot(startDir: string): string {
  let dir = startDir;
  while (true) {
    if (existsSync(join(dir, "package.json"))) return dir;
    const parent = dirname(dir);
    if (parent === dir) {
      throw new Error(`Cannot locate package root from ${startDir}`);
    }
    dir = parent;
  }
}

export type TemplateName =
  | "CLAUDE.md"
  | "settings.json"
  | "project/tech-stack.md"
  | "project/conventions.md"
  | "project/architecture.md"
  | "project/domain.md"
  | "project/gotchas.md";

export type HookName = "post-merge" | "pre-push";

export async function loadTemplate(name: TemplateName): Promise<string> {
  return await readText(join(TEMPLATES_ROOT, `${name}.tpl`));
}

export async function renderTemplateByName(
  name: TemplateName,
  variables: Record<string, string | number | undefined>,
): Promise<string> {
  const source = await loadTemplate(name);
  return renderTemplate(source, variables);
}

export async function loadHook(name: HookName): Promise<string> {
  return await readText(join(HOOKS_ROOT, `${name}.sh.tpl`));
}
